import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import EmptyState from "@/components/ui/empty-state";
import { SelectList, SelectListItem } from "@/components/ui/select-list";
import { ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";
import { Badge } from "@/components/ui/badge";
import { CURRENCY_CODES, CURRENCY_SYMBOLS, currencySymbol, formatCompact, type CurrencyCode } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { CopyButton } from "@/components/ui/copy-button";
import CountryFlag from "react-country-flag";
import {
  AlertCircleIcon,
  ArrowLeft,
  ArrowRight,
  PencilIcon,
  PercentIcon,
  UserRoundPenIcon,
  MailIcon,
  CheckIcon,
  XIcon,
} from "lucide-react";
import { COUNTRIES } from "@/components/dashboard_create_campaign/CampaignFormStep2";
import google from "@/assets/google.svg";
import microsoft from "@/assets/microsoft.svg";
import youtube from "@/assets/youtube.svg";
import tiktok from "@/assets/tiktok.svg";
import instagram from "@/assets/instagram.svg";
import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { useCampaignStore } from "@/stores";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FramePanel,
  FrameAction,
} from "@/components/ui/frame";
import { ShareLinkFrame } from "./ShareLinkFrame";
import { isEveCampaign } from "@/lib/eve";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface MailboxUser {
  user_id: string;
  clerk_user_id: string;
  external_account_id: string;
  name: string;
  email: string;
  imageUrl: string;
  provider: string;
  campaigns_count: number;
}

const InfoItem = ({
  label,
  value,
  svg,
  maxLength = 30,
  action,
  editor,
  onDoubleClick,
}: {
  label: string;
  value: React.ReactNode;
  svg?: React.ReactNode;
  maxLength?: number;
  /** Rendered beside the label (e.g. an edit pencil). */
  action?: React.ReactNode;
  /** When set, rendered in place of the value (inline editing). */
  editor?: React.ReactNode;
  /** Double-clicking the value — the second way into inline editing. */
  onDoubleClick?: () => void;
}) => {
  const displayValue =
    typeof value === "string" ? value.replace(/_/g, " ") : value;
  const isLong =
    typeof displayValue === "string" && displayValue.length > maxLength;

  return (
    <div className="group/info">
      <h3 className="text-xs text-muted-foreground mb-0.5 flex items-center gap-1">
        {label}
        {action}
      </h3>
      {editor ?? (
      <p
        className="capitalize text-sm flex flex-row items-center"
        onDoubleClick={onDoubleClick}
      >
        {svg && <span className="mr-1">{svg}</span>}
        {isLong ? (
          <HoverCard openDelay={300} closeDelay={100}>
            <HoverCardTrigger className="block cursor-default hover:underline data-[state=open]:underline underline-offset-2 max-w-[200px] overflow-hidden whitespace-nowrap mask-[linear-gradient(to_right,black_70%,transparent)] truncate">
              {displayValue}
            </HoverCardTrigger>
            <HoverCardContent
              side="top"
              className="w-auto max-w-80 bg-muted dark:bg-sidebar p-0.5 rounded-lg gap-0 relative group border-0"
            >
              <p className="text-xs wrap-break-word p-2.5 pr-4 bg-background rounded-md border">
                {displayValue}
              </p>
              <CopyButton
                buttonText=""
                className="hover:bg-muted-foreground/10 text-muted-foreground size-7 [&_svg]:size-3.5 rounded-md absolute top-1.25 right-1.25 opacity-0 group-hover:opacity-100 transition-opacity"
                textToCopy={String(displayValue)}
                variant="ghost"
                scope={false}
                tooltipText="Copy!"
                tooltipDelay={500}
              />
            </HoverCardContent>
          </HoverCard>
        ) : (
          displayValue
        )}
      </p>
      )}
    </div>
  );
};

/** The pencil beside an editable label — revealed on hover of the item (like
 *  the title row's copy/refresh buttons), click toggles the inline editor. */
const EditPencil = ({
  active,
  onClick,
}: {
  active?: boolean;
  onClick: () => void;
}) => (
  <Button
    variant="ghost"
    size="icon-sm"
    className={cn(
      "size-5 [&_svg]:size-3 text-muted-foreground hover:bg-muted-foreground/10",
      "opacity-0 group-hover/info:opacity-100 focus-visible:opacity-100 transition-opacity",
      active && "opacity-100 bg-muted-foreground/10 text-foreground",
    )}
    onClick={onClick}
  >
    <PencilIcon />
  </Button>
);

/** Inline Yes/No picker for a boolean value; picking saves and closes. */
const YesNoEditor = ({
  value,
  disabled,
  onPick,
}: {
  value: boolean;
  disabled?: boolean;
  onPick: (next: boolean) => void;
}) => (
  <div className="flex items-center gap-1">
    {[true, false].map((option) => (
      <Button
        key={String(option)}
        size="sm"
        variant={value === option ? "neutral" : "outline"}
        className="h-7 px-2.5 text-xs"
        disabled={disabled}
        onClick={() => onPick(option)}
      >
        {option ? "Yes" : "No"}
      </Button>
    ))}
  </div>
);

/** Inline currency picker; picking saves and closes. Amy-run campaigns are
 *  USD-only, so the other codes are disabled there — with a tooltip saying
 *  why (a disabled button gets no hover, so the span carries it). */
const CurrencyEditor = ({
  value,
  usdOnly,
  note,
  disabled,
  onPick,
}: {
  value: string;
  usdOnly?: boolean;
  /** Shown under the buttons (e.g. what a change does to a live campaign). */
  note?: string;
  disabled?: boolean;
  onPick: (next: CurrencyCode) => void;
}) => (
  <div className="flex flex-col gap-1">
    <TooltipProvider>
      <div className="flex items-center gap-1">
        {CURRENCY_CODES.map((code) => {
          const button = (
            <Button
              key={code}
              size="sm"
              variant={value === code ? "neutral" : "outline"}
              className="h-7 px-2.5 text-xs"
              disabled={disabled || (usdOnly && code !== "USD")}
              onClick={() => onPick(code)}
            >
              {CURRENCY_SYMBOLS[code]} {code}
            </Button>
          );
          if (!usdOnly || code === "USD") return button;
          return (
            <Tooltip key={code}>
              <TooltipTrigger asChild>
                <span>{button}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                Amy campaigns are USD only
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
    {note && <p className="text-xs text-muted-foreground max-w-60">{note}</p>}
  </div>
);

/** Inline number box; saves on blur/Enter, Escape cancels. */
const NumberEditor = ({
  value,
  maxDigits,
  placeholder,
  suffix,
  disabled,
  onChange,
  onSave,
  onCancel,
}: {
  value: string;
  maxDigits: number;
  placeholder?: string;
  suffix?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) => (
  <div className="flex items-center gap-1.5">
    <Input
      autoFocus
      className="h-7 w-16 text-sm"
      inputMode="numeric"
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onFocus={(e) => e.target.select()}
      onChange={(e) =>
        onChange(e.target.value.replace(/\D/g, "").slice(0, maxDigits))
      }
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") onCancel();
      }}
      onBlur={onSave}
    />
    {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
  </div>
);

export function CampaignInfoSection() {
  const {
    campaign: c,
    campaignId,
    updateCampaignField,
    updateCampaignOwner,
    refreshCampaign,
    updatingCampaignField,
  } = useCampaignStore();
  const { getToken, orgId } = useAuth();

  const [campaignName, setCampaignName] = useState<string>(
    c?.campaign_name || "",
  );

  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [mailboxUsers, setMailboxUsers] = useState<MailboxUser[]>([]);
  const [loadingMailboxUsers, setLoadingMailboxUsers] = useState(false);
  const [savingOwner, setSavingOwner] = useState(false);
  const [ownerStep, setOwnerStep] = useState<"pick" | "confirm">("pick");
  const [selectedMailbox, setSelectedMailbox] = useState<MailboxUser | null>(
    null,
  );

  // The owner is editable only while the campaign is in Search (the backend
  // enforces the same lock). Amy-run campaigns included since 2026-10-13: the
  // owner supplies the sender name — the same user_id + external_account_id
  // write Amy's update_campaign makes — while email still goes out from the
  // pool mailbox pinned in the frame below.
  const isEve = isEveCampaign(c);
  const canChangeOwner = c?.status?.toUpperCase() === "SEARCH";

  const handleUpdateCampaignField = async (
    field: string,
    value: string | boolean | number,
  ) => {
    const token = await getToken();
    if (!token || !campaignId) return console.error("No token found");
    await updateCampaignField(field, value, campaignId, token);
  };

  // Inline editing of the Info grid (pencil or double-click): which value is
  // currently swapped for its editor. Both engines read these fields live at
  // every email, so a change applies from the next one.
  type EditKey = "language" | "smart" | "pct" | "deliverables" | "currency";
  const [editing, setEditing] = useState<EditKey | null>(null);
  const toggleEditing = (key: EditKey) =>
    setEditing((cur) => (cur === key ? null : key));

  const saveYesNo = (
    field: "use_creator_native_language" | "enable_smart_negotiation",
    current: boolean,
    next: boolean,
  ) => {
    setEditing(null);
    if (next !== current) handleUpdateCampaignField(field, next);
  };

  // Percentage: saves on blur/Enter when valid (1–99) and changed, otherwise
  // snaps back to the stored value. A missing value reads as 80 downstream.
  const serverPct = c?.smart_negotiation_percentage || "";
  const [pct, setPct] = useState<string>(String(serverPct));
  useEffect(() => {
    setPct(String(serverPct));
  }, [serverPct]);
  const handleSavePct = () => {
    setEditing(null);
    if (pct === String(serverPct)) return;
    if (!/^\d{1,2}$/.test(pct) || Number(pct) < 1) return setPct(String(serverPct));
    handleUpdateCampaignField("smart_negotiation_percentage", Number(pct));
  };

  // Deliverables: multiplies max_budget/max_cpm budgets (not custom formulas)
  // from the next negotiation reply. Valid is 1–99; a missing value reads as 1.
  const serverDeliverables = String(c?.number_of_deliverables || 1);
  const [deliverables, setDeliverables] = useState<string>(serverDeliverables);
  useEffect(() => {
    setDeliverables(serverDeliverables);
  }, [serverDeliverables]);
  const handleSaveDeliverables = () => {
    setEditing(null);
    if (deliverables === serverDeliverables) return;
    if (!/^\d{1,2}$/.test(deliverables) || Number(deliverables) < 1)
      return setDeliverables(serverDeliverables);
    handleUpdateCampaignField("number_of_deliverables", Number(deliverables));
  };

  // Currency is editable at any time (Amy campaigns: USD only). After launch a
  // change re-reads existing budgets and prices in the new currency, so the
  // picker says so. The save also swaps the symbol in the tier descriptions,
  // so the campaign is re-read.
  const currency = c?.currency ?? "USD";
  const isLaunched = c?.status?.toUpperCase() !== "SEARCH";
  const handleSaveCurrency = async (next: CurrencyCode) => {
    setEditing(null);
    if (next === currency) return;
    await handleUpdateCampaignField("currency", next);
    const token = await getToken();
    if (token && campaignId && orgId) refreshCampaign(campaignId, orgId, token);
  };

  useEffect(() => {
    if (!ownerModalOpen) {
      setSelectedMailbox(null);
      setSavingOwner(false);
      setOwnerStep("pick");
      return;
    }
    if (!orgId) return;
    let cancelled = false;
    const loadMailboxUsers = async () => {
      setLoadingMailboxUsers(true);
      try {
        const token = await getToken();
        if (!token) return;
        const response = await fetch(
          `${API_BASE_URL}/api/settings/get-mailbox-users`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ organization_id: orgId }),
          },
        );
        const data = await response.json();
        if (!cancelled && data?.success) {
          const users: MailboxUser[] = data.data || [];
          setMailboxUsers(users);
          // Preselect the currently-assigned owner, if any.
          setSelectedMailbox(
            users.find(
              (u) => u.external_account_id === c?.external_account_id,
            ) ?? null,
          );
        }
      } finally {
        if (!cancelled) setLoadingMailboxUsers(false);
      }
    };
    loadMailboxUsers();
    return () => {
      cancelled = true;
    };
  }, [ownerModalOpen, orgId, getToken, c?.external_account_id]);

  const handleConfirmOwner = async () => {
    if (!campaignId || !orgId || !selectedMailbox) return;
    setSavingOwner(true);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await updateCampaignOwner(
        campaignId,
        orgId,
        selectedMailbox,
        token,
      );
      if (result.success) setOwnerModalOpen(false);
    } finally {
      setSavingOwner(false);
    }
  };

  return (
    <div className="space-y-6">
      <Frame>
        <FrameHeader>
          <FrameTitle>Name & Status</FrameTitle>
        </FrameHeader>
        <FramePanel className="flex items-center gap-2">
          <Input
            className="w-4/5 lg:w-1/2 font-medium"
            placeholder="A name to remember . . ."
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
          />
          {campaignName !== c?.campaign_name && (
            <Button
              size="icon"
              className="animate-in fade-in-0 slide-in-from-left-2 duration-200"
              disabled={updatingCampaignField}
              onClick={() => handleUpdateCampaignField("campaign_name", campaignName)}
            >
              {updatingCampaignField ? <Spinner /> : <CheckIcon />}
            </Button>
          )}
          {campaignName !== c?.campaign_name && (
            <Button
              size="icon"
              className="animate-in fade-in-0 slide-in-from-left-4 duration-200"
              variant="outline"
              disabled={updatingCampaignField}
              onClick={() => setCampaignName(c?.campaign_name || "")}
            >
              <XIcon />
            </Button>
          )}
          <Badge variant="secondary" className="capitalize ml-auto px-2 py-1">
            {c?.status?.toLowerCase()}
          </Badge>
        </FramePanel>
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Info</FrameTitle>
        </FrameHeader>
        <FramePanel>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InfoItem
                label="Platform"
                value={
                  c?.platform +
                  (c?.youtube_content_type
                    ? ` - ${c.youtube_content_type.replace("_", " ")}`
                    : "")
                }
                svg={
                  c?.platform === "youtube" ? (
                    <img src={youtube} alt="YouTube" className="h-3" />
                  ) : c?.platform === "tiktok" ? (
                    <img src={tiktok} alt="TikTok" className="size-3.5" />
                  ) : c?.platform === "instagram" ? (
                    <img src={instagram} alt="Instagram" className="size-3.5" />
                  ) : null
                }
              />
              <InfoItem
                label="Currency"
                value={`${currency} (${currencySymbol(currency)})`}
                action={
                  <EditPencil
                    active={editing === "currency"}
                    onClick={() => toggleEditing("currency")}
                  />
                }
                onDoubleClick={() => setEditing("currency")}
                editor={
                  editing === "currency" ? (
                    <CurrencyEditor
                      value={currency}
                      usdOnly={isEve}
                      note={
                        isLaunched && !isEve
                          ? "Existing budgets and agreed prices keep their numbers in the new currency."
                          : undefined
                      }
                      disabled={updatingCampaignField}
                      onPick={handleSaveCurrency}
                    />
                  ) : undefined
                }
              />
              <InfoItem
                label="Requested Creators (5x)"
                value={c?.number_of_creators}
              />
              <InfoItem
                label="Followers"
                value={
                  c?.follower_range_from !== undefined &&
                    c?.follower_range_to !== undefined
                    ? `${formatCompact(c.follower_range_from)} - ${formatCompact(
                      c.follower_range_to,
                    )}`
                    : "Not specified"
                }
              />
              {(!!c?.median_views_from || !!c?.median_views_to) && (
                <InfoItem
                  label="Median Views"
                  value={
                    c.median_views_from && c.median_views_to
                      ? `${formatCompact(c.median_views_from)} - ${formatCompact(c.median_views_to)}`
                      : c.median_views_from
                        ? `${formatCompact(c.median_views_from)}+`
                        : `Up to ${formatCompact(c.median_views_to!)}`
                  }
                />
              )}
              <InfoItem
                label="Number of Deliverables"
                value={c?.number_of_deliverables || 1}
                action={
                  <EditPencil
                    active={editing === "deliverables"}
                    onClick={() => toggleEditing("deliverables")}
                  />
                }
                onDoubleClick={() => setEditing("deliverables")}
                editor={
                  editing === "deliverables" ? (
                    <NumberEditor
                      value={deliverables}
                      maxDigits={2}
                      placeholder="1"
                      disabled={updatingCampaignField}
                      onChange={setDeliverables}
                      onSave={handleSaveDeliverables}
                      onCancel={() => {
                        setDeliverables(serverDeliverables);
                        setEditing(null);
                      }}
                    />
                  ) : undefined
                }
              />
              {/* Partnership Tiers */}
              {c?.partnership?.tiers && (
                <>
                  <InfoItem
                    label="Partnership"
                    value={`${c.partnership.tiers.length} tier${c.partnership.tiers.length > 1 ? "s" : ""}`}
                  />
                  {c.partnership.tiers.map((tier, idx) => (
                    <InfoItem
                      key={idx}
                      label={`Tier ${tier.order}${idx === 0 ? " (Primary)" : " (Fallback)"}`}
                      value={tier.components
                        ?.map(
                          (comp) =>
                            comp.description || comp.type?.replace("_", " "),
                        )
                        .join(" + ")}
                    />
                  ))}
                </>
              )}
              <InfoItem
                label="Use Creator Native Language"
                value={c?.use_creator_native_language ? "Yes" : "No"}
                action={
                  <EditPencil
                    active={editing === "language"}
                    onClick={() => toggleEditing("language")}
                  />
                }
                onDoubleClick={() => setEditing("language")}
                editor={
                  editing === "language" ? (
                    <YesNoEditor
                      value={!!c?.use_creator_native_language}
                      disabled={updatingCampaignField}
                      onPick={(next) =>
                        saveYesNo(
                          "use_creator_native_language",
                          !!c?.use_creator_native_language,
                          next,
                        )
                      }
                    />
                  ) : undefined
                }
              />
              <InfoItem
                label="Smart Negotiation Enabled"
                value={c?.enable_smart_negotiation ? "Yes" : "No"}
                action={
                  <EditPencil
                    active={editing === "smart"}
                    onClick={() => toggleEditing("smart")}
                  />
                }
                onDoubleClick={() => setEditing("smart")}
                editor={
                  editing === "smart" ? (
                    <YesNoEditor
                      value={!!c?.enable_smart_negotiation}
                      disabled={updatingCampaignField}
                      onPick={(next) =>
                        saveYesNo(
                          "enable_smart_negotiation",
                          !!c?.enable_smart_negotiation,
                          next,
                        )
                      }
                    />
                  ) : undefined
                }
              />
              {c?.enable_smart_negotiation && (
                <InfoItem
                  label="Smart Negotiation Percentage"
                  value={c?.smart_negotiation_percentage}
                  svg={
                    <PercentIcon className="size-3.5 text-muted-foreground" />
                  }
                  action={
                    <EditPencil
                      active={editing === "pct"}
                      onClick={() => toggleEditing("pct")}
                    />
                  }
                  onDoubleClick={() => setEditing("pct")}
                  editor={
                    editing === "pct" ? (
                      <NumberEditor
                        value={pct}
                        maxDigits={2}
                        placeholder="80"
                        suffix="%"
                        disabled={updatingCampaignField}
                        onChange={setPct}
                        onSave={handleSavePct}
                        onCancel={() => {
                          setPct(String(serverPct));
                          setEditing(null);
                        }}
                      />
                    ) : undefined
                  }
                />
              )}
              <InfoItem
                label="Exclude Past Campaign Creators"
                value={c?.exclude_past_campaign_creators ? "Yes" : "No"}
              />
              <InfoItem
                label="Exclude Creators from Exclusion List"
                value={c?.exclude_excluded_creators ? "Yes" : "No"}
              />
            </div>
            <div>
              <h3 className="text-xs text-muted-foreground mb-1">
                Creator countries
              </h3>
              <p className="flex flex-wrap gap-2">
                {COUNTRIES.filter((country) =>
                  c?.countries?.includes(country.value),
                ).map((country) => (
                  <div
                    key={country.value}
                    className="bg-muted dark:bg-sidebar rounded-sm px-2 py-1 text-sm gap-2 flex items-center"
                  >
                    <CountryFlag
                      svg
                      className="rounded-xs min-w-4.5"
                      countryCode={country.value}
                      title={country.label}
                    />
                    {country.label}
                  </div>
                ))}
              </p>
            </div>
          </div>
        </FramePanel>
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Search Creator Prompt</FrameTitle>
          <FrameAction>
            <CopyButton
              buttonText=""
              className="size-7 text-muted-foreground hover:bg-muted-foreground/10 [&_svg]:size-3.5"
              textToCopy={c?.creator_prompt || ""}
              variant="ghost"
              tooltipText="Copy prompt"
              tooltipDelay={500}
            />
          </FrameAction>
        </FrameHeader>
        <FramePanel className="text-sm">{c?.creator_prompt}</FramePanel>
      </Frame>

      <ShareLinkFrame />

      <Frame>
        <FrameHeader>
          <FrameTitle>{isEve ? "Campaign Owner" : "Campaign Owner & Inbox"}</FrameTitle>
        </FrameHeader>
        <FramePanel className="flex items-center gap-2 p-3">
          <img
            src={c?.campaign_owner?.avatar || "/placeholder.svg"}
            alt={c?.campaign_owner?.name}
            className="size-10 rounded-md border"
          />
          <div className="flex flex-col gap-0.5">
            <p className="font-medium text-sm">{c?.campaign_owner?.name}</p>
            <div className="flex flex-row items-center gap-1 text-muted-foreground text-xs">
              <img
                src={
                  c?.campaign_owner?.provider === "oauth_google"
                    ? google
                    : microsoft
                }
                alt={"Provider:"}
                className="size-3.25"
              />
              {c?.campaign_owner?.provider === "oauth_google"
                ? "Google"
                : "Microsoft"}
              <p>{"•"}</p>
              <p className="">{c?.campaign_owner?.email}</p>
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-auto">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!canChangeOwner}
                    onClick={() => setOwnerModalOpen(true)}
                  >
                    <UserRoundPenIcon />
                    {isEve ? "Change Owner" : "Change Owner & Inbox"}
                  </Button>
                </span>
              </TooltipTrigger>
              {!canChangeOwner && (
                <TooltipContent side="top">
                  {isEve
                    ? "Campaign has started. Owner cannot be changed."
                    : "Campaign has started. Owner and inbox cannot be changed."}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </FramePanel>
      </Frame>

      <AlertDialog open={ownerModalOpen} onOpenChange={setOwnerModalOpen}>
        <AlertDialogContent className="max-w-lg">
          {ownerStep === "pick" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {isEve ? "Select Campaign Owner" : "Select Campaign Owner & Inbox"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isEve
                    ? "Choose which user owns this campaign. Amy signs outreach with the owner's name; emails still go out from Amy's mailbox."
                    : "Choose which user and mailbox will send outreach for this campaign."}
                </AlertDialogDescription>
              </AlertDialogHeader>

              {loadingMailboxUsers ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner className="text-muted-foreground size-5" />
                </div>
              ) : mailboxUsers.length === 0 ? (
                <EmptyState
                  icon={<MailIcon />}
                  text="No members with a connected Gmail or Outlook account"
                />
              ) : (
                <SelectList
                  value={selectedMailbox?.external_account_id ?? ""}
                  onValueChange={(id) =>
                    setSelectedMailbox(
                      mailboxUsers.find((m) => m.external_account_id === id) ?? null,
                    )
                  }
                  className="max-h-100"
                >
                  {mailboxUsers.map((m) => (
                    <SelectListItem key={m.external_account_id} value={m.external_account_id}>
                      <ItemMedia>
                        <Avatar variant="square">
                          <AvatarImage src={m.imageUrl || ""} />
                          <AvatarFallback>{m.name ? m.name.charAt(0) : "U"}</AvatarFallback>
                        </Avatar>
                      </ItemMedia>
                      <ItemContent className="min-w-0 gap-0">
                        <ItemTitle>
                          {m.name}
                          {c?.external_account_id === m.external_account_id && (
                            <Badge variant="flat" size="sm">Current</Badge>
                          )}
                          {m.campaigns_count > 0 && (
                            <Badge variant="muted" size="sm">
                              {m.campaigns_count} campaign{m.campaigns_count === 1 ? "" : "s"}
                            </Badge>
                          )}
                        </ItemTitle>
                        <ItemDescription className="flex items-center gap-1">
                          <img
                            src={m.provider === "oauth_google" ? google : microsoft}
                            alt={m.provider === "oauth_google" ? "Google" : "Microsoft"}
                            className="size-3"
                          />
                          <span className="truncate">{m.email}</span>
                        </ItemDescription>
                      </ItemContent>
                    </SelectListItem>
                  ))}
                </SelectList>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button
                  onClick={() => setOwnerStep("confirm")}
                  disabled={
                    !selectedMailbox ||
                    c?.external_account_id === selectedMailbox.external_account_id ||
                    savingOwner
                  }
                  variant="neutral"
                >
                  Next
                  <ArrowRight />
                </Button>
              </AlertDialogFooter>
            </>
          )}

          {ownerStep === "confirm" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {isEve ? "Change owner?" : "Change owner & inbox?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isEve
                    ? "Amy will sign outreach with the name of "
                    : "Outreach for this campaign will be sent from "}
                  <span className="font-medium text-foreground">
                    {selectedMailbox?.name || "the selected user"}
                  </span>
                  {selectedMailbox?.email ? ` (${selectedMailbox.email})` : ""}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Alert variant="warning">
                <AlertTitle>
                  <AlertCircleIcon />
                  {isEve
                    ? "This changes the name Amy signs with."
                    : "This changes who sends the campaign's emails."}
                </AlertTitle>
                <AlertDescription>
                  Only allowed while the campaign is in Search.
                </AlertDescription>
              </Alert>
              <AlertDialogFooter>
                <Button
                  variant="secondary"
                  onClick={() => setOwnerStep("pick")}
                  disabled={savingOwner}
                >
                  <ArrowLeft />
                  Back
                </Button>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirmOwner();
                  }}
                  variant="neutral"
                  disabled={savingOwner}
                >
                  {savingOwner && <Spinner />}
                  {!savingOwner && <UserRoundPenIcon />}
                  Change Owner
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
